/**
 * This TransferModal is the standard UI flow for transferring a single source sample (entity, batches
 * (deprecated), or container) into multiple output containers. There are 3 configurations for the high level
 * flow of the modal specified in `TRANSFER_FLOWS`, and the pane flow ordering is specified in `getPaneOrderByFlow`.
 */
import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import * as Analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DEFAULT_LEGACY_MEASUREMENT, LegacyMeasurement} from 'constants/MeasurementConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchemaContainableType} from 'constants/RegistryConstants';
import {
  AccessibleGridCell,
  ContainerSummary,
  GridSummary,
  MODAL_PANE,
  ModalPane,
  TransferFlow,
  TransferModalPane,
} from 'constants/SampleConstants';
import {SampleControlParams, SampleRestrictionStatus} from 'constants/SampleStatusConstants';
import * as Context from 'Context';
import {BioentitySummary} from 'folder-items/FileConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import preferences from 'Preferences';
import ConfirmSampleControlsPane, {
  supportsValidRestrictionStatus,
} from 'samples/transfers/ConfirmSampleControlsPane';
import * as InputSampleConstants from 'samples/transfers/input-sample/InputSampleConstants';
import {TYPE as InputSampleType} from 'samples/transfers/input-sample/InputSampleConstants';
import * as TransferInputPaneStateUtil from 'samples/transfers/input-sample/PaneStateUtil';
import * as TransferActions from 'samples/transfers/TransferActions';
import * as TransferModalUtil from 'samples/transfers/TransferModalUtil';
import {
  ContainerSummaryWithContainedItemIds,
  OutputSampleType,
  StorableModalsContainerContent,
  StorableModalsContainerTransfer,
  TRANSFER_MODAL_PREFERENCES,
  TransferModalState,
  TransferPaneSampleStatusState,
} from 'samples/transfers/TransferModalUtil';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import InlineBanner, {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {UnitTypesQuery} from 'units/__generated/UnitTypesQuery';
import {UNIT_TYPES_QUERY} from 'units/UnitsConstants';
import {Optional} from 'util/Types';

import ChooseInputPane, {
  getIncompatibleUnitsOutputContainers as getIncompatibleContainersForChooseInput,
} from './ChooseInputPane';
import ChooseOutputPane, {
  getIncompatibleUnitsOutputContainers as getIncompatibleContainersForChooseOutput,
} from './ChooseOutputPane';
import ConfirmConcentrationsPane from './ConfirmConcentrationsPane';
import ConfirmTransferQuantitiesPane, {
  getIncompatibleUnitsRowInfos as getIncompatibleRowsForConfirmQuantities,
} from './ConfirmTransferQuantitiesPane';

const {CHOOSE_TRANSFER_INPUT, CHOOSE_TRANSFER_OUTPUT, CONFIRM_TRANSFER_QUANTITIES, CONFIRM_CONCENTRATIONS} =
  MODAL_PANE;

// Export for tests
export function getPaneOrderByFlow(flow: TransferFlow): Array<TransferModalPane> {
  const sampleControlPanes: Array<TransferModalPane> = hasMinimumSCRFeatureLevel(
    SampleControlsFeatureLevel.V3
  )
    ? [ModalPane.CONFIRM_SAMPLE_CONTROLS]
    : [];
  switch (flow) {
    case TransferFlow.TRANSFER_OUT:
      return [
        ModalPane.CHOOSE_TRANSFER_OUTPUT,
        ModalPane.CONFIRM_TRANSFER_QUANTITIES,
        ModalPane.CONFIRM_CONCENTRATIONS,
        ...sampleControlPanes,
      ];
    case TransferFlow.TRANSFER_IN:
      return [
        ModalPane.CHOOSE_TRANSFER_INPUT,
        ModalPane.CONFIRM_TRANSFER_QUANTITIES,
        ModalPane.CONFIRM_CONCENTRATIONS,
        ...sampleControlPanes,
      ];
    case TransferFlow.TRANSFER_GLOBAL:
      return [
        ModalPane.CHOOSE_TRANSFER_INPUT,
        ModalPane.CHOOSE_TRANSFER_OUTPUT,
        ModalPane.CONFIRM_TRANSFER_QUANTITIES,
        ModalPane.CONFIRM_CONCENTRATIONS,
        ...sampleControlPanes,
      ];
    default:
      assert(false, 'Unexpected transfer flow');
      return [];
  }
}

function skipConcentrationPane(paneState: Optional<TransferModalState>): boolean {
  const confirmTransferQuantitiesData = paneState?.confirmTransferQuantitiesData ?? null;
  const outputContainers = confirmTransferQuantitiesData?.map((data) => data.outputContainer);
  return outputContainers != null && outputContainers.every((c) => !c.quantity.value && !c.contents.length);
}

function shouldSkipSampleControlsPane(paneState: Optional<TransferModalState>): boolean {
  const confirmTransferQuantitiesData = paneState?.confirmTransferQuantitiesData ?? null;
  const outputContainers = confirmTransferQuantitiesData?.map((data) => data.outputContainer);
  return outputContainers != null && outputContainers.every((c) => !supportsValidRestrictionStatus(c));
}

function getPrevPane(
  pane: Optional<TransferModalPane>,
  flow: TransferFlow,
  paneState: Optional<TransferModalState>
): TransferModalPane {
  // Note that undefined is used to represent "no pane available", so getPaneOrderByFlow(flow)[-1] is valid
  const paneIndex = pane ? getPaneOrderByFlow(flow).indexOf(pane) : -1;
  const prevPaneInFlow = getPaneOrderByFlow(flow)[paneIndex - 1];
  if (prevPaneInFlow === CONFIRM_CONCENTRATIONS && skipConcentrationPane(paneState)) {
    return getPaneOrderByFlow(flow)[paneIndex - 2];
  } else if (
    prevPaneInFlow === ModalPane.CONFIRM_SAMPLE_CONTROLS &&
    shouldSkipSampleControlsPane(paneState)
  ) {
    const offsetIdx = skipConcentrationPane(paneState) ? 3 : 2;
    return getPaneOrderByFlow(flow)[paneIndex - offsetIdx];
  }
  return prevPaneInFlow;
}

function getNextPane(
  pane: Optional<TransferModalPane>,
  flow: TransferFlow,
  paneState: Optional<TransferModalState>
): TransferModalPane | null {
  // Note that undefined is used to represent "no pane available", so getPaneOrderByFlow(flow)[-1] is valid
  const paneIndex = pane ? getPaneOrderByFlow(flow).indexOf(pane) : -1;
  const nextPaneInFlow = getPaneOrderByFlow(flow)[paneIndex + 1];
  if (nextPaneInFlow === CONFIRM_CONCENTRATIONS && skipConcentrationPane(paneState)) {
    // If CONFIRM_CONCENTRATIONS would've been last, skips to sample controls pane if present, otherwise
    // returns undefined
    const offsetIdx = shouldSkipSampleControlsPane(paneState) ? 3 : 2;
    return getPaneOrderByFlow(flow)[paneIndex + offsetIdx];
  } else if (
    nextPaneInFlow === ModalPane.CONFIRM_SAMPLE_CONTROLS &&
    shouldSkipSampleControlsPane(paneState)
  ) {
    return getPaneOrderByFlow(flow)[paneIndex + 2];
  }
  return nextPaneInFlow;
}

export function openTransferModal(props: Omit<TransferModalProps, 'onRequestClose'>): void {
  LegacyModalContainer.open(TransferModal, <TransferModal {...props} onRequestClose={closeTransferModal} />);
}

function closeTransferModal(): void {
  LegacyModalContainer.close(TransferModal);
}

export interface TransferModalProps {
  flow: TransferFlow;
  onRequestClose: () => void;
  actionOrigin: ActionOrigin;
  registryId?: string;
  title?: string;
  preselectedInputConfig?: {
    inputType: InputSampleType;
    inputBioentity?: BioentitySummary | null;
    inputContainer?: ContainerSummaryWithContainedItemIds | null;
  };
  preselectedOutputConfig?: {
    outputType: OutputSampleType;
    outputPlate: GridSummary | null;
    outputCells: Array<AccessibleGridCell>;
    outputContainerSummaries?: Array<ContainerSummary>;
  };
  onSuccess?: () => void;
  inputPaneInfoBannerMessage?: string | null;
}

type State = TransferModalState;
export class TransferModal extends React.Component<TransferModalProps, State> {
  static defaultProps = {
    title: 'Transfer samples',
  };

  constructor(props: TransferModalProps) {
    super(props);
    const baseState = TransferInputPaneStateUtil.getDefaultPaneState();
    // Override transfer modal state from preferences
    Object.values(TRANSFER_MODAL_PREFERENCES).forEach(({stateKey, preferenceKey}) => {
      const prefValue = preferences.get(preferenceKey);
      if (prefValue !== undefined) {
        // @ts-expect-error
        baseState[stateKey] = prefValue;
      }
    });
    const initialRestrictionState = TransferInputPaneStateUtil.getInitialRestrictionStatusState(
      this.props.preselectedInputConfig?.inputContainer
    );
    const initialState: TransferModalState = {
      ...baseState,
      ...this.props.preselectedInputConfig,
      ...this.props.preselectedOutputConfig,
      ...initialRestrictionState,
      pane: getPaneOrderByFlow(this.props.flow)[0],
      // inputContainableConcentration=null normally means to use the concentration from the source, whereas
      // DEFAULT_LEGACY_MEASUREMENT `{units: null, value: null}` normally means to explicitly set the
      // concentration as unspecified. Historically, the calculate-concentrations endpoint, where this value
      // will be sent, treated these as equivalent, if the source was a container. But we want to send `null`
      // in that case for clarity and consistency, and we may want to change the backend to treat these
      // differently at some point. In other transfer modal flows, this value is set to null on the transition
      // from the CHOOSE_TRANSFER_INPUT pane. See completeTransferInputPane and completeAndGetModalState.
      inputContainableConcentration:
        this.props.preselectedInputConfig?.inputContainer != null ? null : DEFAULT_LEGACY_MEASUREMENT,
    };
    this.state = {
      ...initialState,
      ...TransferModalUtil.getTransferPaneState(initialState.pane, initialState),
    };
  }

  static getDerivedStateFromProps(_props: TransferModalProps, state: State): State {
    return {
      ...state,
      errors: TransferModalUtil.getErrorBannerMessages(state),
    };
  }

  override componentDidUpdate(prevProps: TransferModalProps, prevState: State): void {
    if (
      prevState.isTransferring &&
      !this.state.transferCancelled &&
      !this.state.isTransferring &&
      !this.state.errorMessage
    ) {
      this.handleClose();
    }
  }

  private handleClose = (): void => {
    this.props.onRequestClose();
  };

  private handleGoToPrevPane = (): void => {
    const prevPane = getPrevPane(this.state.pane, this.props.flow, this.state);
    this.handleUpdateState({pane: prevPane, errorMessage: null});
  };

  baseSampleStatusTrackingParams: BaseSampleStatusWarningTrackingParams = {
    actionAttempted: 'transfer',
    actionOrigin: this.props.actionOrigin,
    eventGroupId: Analytics.makeAnalyticsEventId(),
  };

  private handleUpdateSampleStatusState = (
    statusState: Partial<TransferPaneSampleStatusState>,
    statusStateKey: 'inputSampleStatusState' | 'outputSampleStatusState'
  ): void => {
    this.setState(
      (oldState) =>
        ({[statusStateKey]: {...oldState[statusStateKey], ...statusState}}) as Pick<
          State,
          'inputSampleStatusState' | 'outputSampleStatusState'
        >
    );
  };

  private handleUpdateState = (state: Partial<TransferModalState>): void => {
    // Update preferences for default transfer configuration
    Object.values(TRANSFER_MODAL_PREFERENCES).forEach(({stateKey, preferenceKey}) => {
      if (state[stateKey] !== undefined) {
        preferences.set(preferenceKey, state[stateKey]);
      }
    });
    this.setState((oldState) => {
      const updatedState = {
        ...oldState,
        ...state,
        inputType: TransferInputPaneStateUtil.getValidInputType({...oldState, ...state}),
      };
      const updatedPaneState =
        (updatedState.pane && TransferModalUtil.getTransferPaneState(updatedState.pane, updatedState)) ?? {};
      return {
        ...updatedState,
        ...updatedPaneState,
      };
    });
  };

  private handleGoToNextPane = (): void => {
    const nextPane = getNextPane(this.state.pane, this.props.flow, this.state);
    const {outputContainerSummaries, outputSampleStatusState, confirmedOutputContainerIds} = this.state;

    const goToNextPaneFn = (): void => {
      if (nextPane === MODAL_PANE.CONFIRM_TRANSFER_QUANTITIES) {
        if (outputContainerSummaries && outputContainerSummaries.length) {
          this.handleUpdateState({confirmTransferQuantitiesData: null});
          const outputContainerIds = outputContainerSummaries.map((summary) => summary.id);
          const confirmedOutputContainerIdsSet = new Set(confirmedOutputContainerIds);
          const maybeFilteredContainerIds =
            confirmedOutputContainerIds != null &&
            outputSampleStatusState.warningInputOutputType !== ModelTypeConstants.PLATE
              ? outputContainerIds.filter((id) => confirmedOutputContainerIdsSet.has(id))
              : outputContainerIds;
          TransferModalUtil.bulkFetchContainers(
            maybeFilteredContainerIds,
            this.state,
            this.handleUpdateState
          );
        } else {
          this.handleUpdateState({
            confirmTransferQuantitiesData: TransferModalUtil.initialConfirmTransferQuantitiesData(this.state),
          });
        }
      }
      if (nextPane === MODAL_PANE.CONFIRM_CONCENTRATIONS) {
        TransferActions.calculateConcentrations({
          ...TransferModalUtil.getConfirmConcentrationsArgs(this.state),
          onStart: this.handleCalculateConcentrationsStarted,
          onComplete: this.handleCalculateConcentrationsCompleted,
          onFailure: this.handleCalculateConcentrationsFailed,
        });
      }
      if (nextPane != null) {
        this.handleUpdateState({pane: nextPane});
      }
    };

    if (this.state.pane === MODAL_PANE.CHOOSE_TRANSFER_INPUT) {
      TransferModalUtil.completeTransferInputPane(this.state, goToNextPaneFn, this.handleUpdateState);
    } else {
      goToNextPaneFn();
    }
  };

  private shouldBlockForIncompatibleUnits = (): boolean => {
    const paneIndex = getPaneOrderByFlow(this.props.flow).indexOf(this.state.pane);
    switch (this.state.pane) {
      case CHOOSE_TRANSFER_INPUT:
        // We don't want to block moving to the CHOOSE_TRANSFER_OUTPUT pane because the user may want to fix
        // the incompatible units error by changing the output containers,
        return (
          getPaneOrderByFlow(this.props.flow)[paneIndex + 1] !== CHOOSE_TRANSFER_OUTPUT &&
          getIncompatibleContainersForChooseInput(this.state).length > 0
        );
      case CHOOSE_TRANSFER_OUTPUT:
        return getIncompatibleContainersForChooseOutput(this.state).length > 0;
      case CONFIRM_TRANSFER_QUANTITIES:
        return getIncompatibleRowsForConfirmQuantities(this.state).length > 0;
      default:
        return false;
    }
  };

  private getButtons = (): Array<ModalButton> => {
    const prevPane = getPrevPane(this.state.pane, this.props.flow, this.state);
    const nextPane = getNextPane(this.state.pane, this.props.flow, this.state);
    const prevButton = prevPane && {
      onClick: this.handleGoToPrevPane,
      key: `back-${prevPane}`,
      pullLeft: true,
      style: ButtonStyle.TERTIARY_GRAY,
      text: 'Back',
      dataTestAttrs: {component: 'TransferModal', key: ModalButtonKey.BACK},
    };
    const nextButton = nextPane
      ? {
          disabled:
            this.state.errors.length > 0 ||
            !TransferModalUtil.isPaneComplete(this.state.pane, this.state) ||
            this.uncontainableEntityChosen() ||
            this.wantsBatches() ||
            this.shouldBlockForIncompatibleUnits(),
          onClick: this.handleGoToNextPane,
          key: `next-${nextPane}`,
          style: ButtonStyle.PRIMARY,
          text:
            this.state.pane === MODAL_PANE.CHOOSE_TRANSFER_INPUT
              ? TransferInputPaneStateUtil.getCompleteButtonText(this.state)
              : 'Next',
          dataTestAttrs: {component: 'TransferModal', key: ModalButtonKey.NEXT},
        }
      : {
          disabled:
            this.state.errors.length > 0 ||
            this.state.isTransferring ||
            !TransferModalUtil.isPaneComplete(this.state.pane, this.state) ||
            this.shouldBlockForIncompatibleUnits(),
          style: ButtonStyle.PRIMARY,
          submit: true,
          text: this.state.isTransferring ? 'Transferring...' : 'Done',
          dataTestAttrs: {component: 'TransferModal', key: ModalButtonKey.DONE},
        };
    return _.compact([prevButton, nextButton]);
  };

  private getOutputSampleControlParams = (): SampleControlParams => {
    if (this.state.useInferredSampleControls || !hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V3)) {
      return {};
    }
    return {
      restrictionStatus: this.state.restrictionStatus,
      sampleRoles: {
        sampleOwnerIds: this.state.sampleOwners.map(({id}) => id),
        ...(this.state.restrictionStatus === SampleRestrictionStatus.RESTRICTED && {
          restrictedSampleUserIds: this.state.restrictedSampleUsers.map(({id}) => id),
        }),
      },
    };
  };

  private calculateAndTransfer = (): void => {
    Context.usingAsync({analyticsMetadata: {actionOrigin: this.props.actionOrigin}}, null, async () => {
      await TransferActions.calculateConcentrationsAndDoTransfersWithDataMods({
        ...TransferModalUtil.getConfirmConcentrationsArgs(this.state),
        outputSampleControls: this.getOutputSampleControlParams(),
        onStart: this.handleTransferStarted,
        onFinish: this.handleTransferFinished,
      });
    });
  };

  private transfer = (): void => {
    const {containerTransfers, containerContents} = this.state;
    assert(containerTransfers != null, 'containerTransfers should be defined');
    assert(containerContents != null, 'containerContents should be defined');
    Context.usingAsync({analyticsMetadata: {actionOrigin: this.props.actionOrigin}}, null, async () => {
      await TransferActions.doTransfersWithDataMods(
        containerTransfers,
        containerContents,
        this.getOutputSampleControlParams(),
        this.handleTransferStarted,
        (errorMessage) => {
          this.handleTransferFinished(false, errorMessage);
        }
      );
    });
  };

  private handleCalculateConcentrationsStarted = (): void => {
    this.handleUpdateState({containerTransfers: null, containerContents: null});
  };

  private handleCalculateConcentrationsCompleted = (transferParams: {
    containerTransfers: Array<StorableModalsContainerTransfer> | null;
    containerContents: Array<StorableModalsContainerContent> | null;
  }): void => {
    this.handleUpdateState({...transferParams, errorMessage: null});
  };

  private handleCalculateConcentrationsFailed = (errorMessage: string): void => {
    this.handleUpdateState({errorMessage, isTransferring: false});
  };

  private handleTransferStarted = (): void => {
    this.handleUpdateState({isTransferring: true, transferCancelled: false, errorMessage: null});
  };

  private handleTransferFinished = (transferCancelled: boolean, errorMessage?: string): void => {
    const errorState = errorMessage != null ? {errorMessage} : {};
    this.handleUpdateState({...errorState, isTransferring: false, transferCancelled});
    if (!transferCancelled && errorMessage == null) {
      this.props.onSuccess?.();
    }
  };

  private getFinalTransferOrConcentrationPane = (): TransferModalPane => {
    if (
      hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V3) &&
      !shouldSkipSampleControlsPane(this.state)
    ) {
      return getPrevPane(this.state.pane, this.props.flow, this.state);
    } else {
      return this.state.pane;
    }
  };

  private getSubmit = (): (() => void) | null => {
    // Modal warns if onRequestSubmit is present without a "submit" button
    if (getNextPane(this.state.pane, this.props.flow, this.state)) {
      return null;
    } else if (this.getFinalTransferOrConcentrationPane() === CONFIRM_TRANSFER_QUANTITIES) {
      return this.calculateAndTransfer;
    } else if (this.getFinalTransferOrConcentrationPane() === CONFIRM_CONCENTRATIONS) {
      return this.transfer;
    } else {
      throw new Error('Unexpected pane state');
    }
  };

  private wantsBatches = (): boolean => {
    if (InputSampleConstants.HAS_EXISTING_BIOENTITY_TYPES.includes(this.state.inputType)) {
      return this.state.inputBioentity?.tagSchema?.containableType === TagSchemaContainableType.BATCH;
    } else if (InputSampleConstants.HAS_NEW_BIOENTITY_TYPES.includes(this.state.inputType)) {
      return (
        this.state.createAndRegisterBioentityArgs?.tagSchema?.containableType ===
        TagSchemaContainableType.BATCH
      );
    } else {
      return false;
    }
  };

  private uncontainableEntityChosen = (): boolean => {
    if (InputSampleConstants.HAS_EXISTING_BIOENTITY_TYPES.includes(this.state.inputType)) {
      if (this.state.inputBioentity != null) {
        return (
          this.state.inputBioentity.tagSchema == null ||
          this.state.inputBioentity.tagSchema.containableType === TagSchemaContainableType.NONE
        );
      } else {
        return false;
      }
    } else if (InputSampleConstants.HAS_NEW_BIOENTITY_TYPES.includes(this.state.inputType)) {
      return (
        this.state.createAndRegisterBioentityArgs?.tagSchema?.containableType ===
        TagSchemaContainableType.NONE
      );
    } else {
      return false;
    }
  };

  private errorBannerProps = (): InlineBannerProps | null => {
    if (this.wantsBatches()) {
      return {
        bannerType: InlineBannerType.ERROR,
        dataTestAttrs: {component: 'TransferModal', element: 'errorBanner'},
        text: 'This entity has been configured to use batches in containers, but batches have been disabled. If you wish to store this entity in a container, please contact your admin.',
      };
    } else if (this.uncontainableEntityChosen()) {
      return {
        bannerType: InlineBannerType.ERROR,
        dataTestAttrs: {component: 'TransferModal', element: 'errorBanner'},
        text: 'This entity has not been configured to be stored in containers. If you wish to store this entity in a container, please contact your admin.',
      };
    } else {
      return null;
    }
  };

  private handleChangeShouldCalculateConcentrations = (shouldCalculateConcentrations: boolean): void => {
    this.handleUpdateState({shouldCalculateConcentrations});
  };

  private handleChangeConcentration = (
    containerId: string,
    containedItemId: string,
    concentration: Partial<LegacyMeasurement>
  ): void => {
    this.setState((oldState) => {
      assert(
        oldState.containerContents != null,
        'Expected container contents to be non null on concentration pane'
      );
      const index = oldState.containerContents.findIndex(
        (cb) => cb.containedItemId === containedItemId && cb.containerId === containerId
      );
      assert(index !== -1, 'Expected container content to be present');

      const updatedContainerContent = {
        ...oldState.containerContents[index],
        concentration: {...oldState.containerContents[index].concentration, ...concentration},
      };
      const containerContents = [
        ...oldState.containerContents.slice(0, index),
        updatedContainerContent,
        ...oldState.containerContents.slice(index + 1),
      ];
      const updatedPaneState =
        (oldState.pane &&
          TransferModalUtil.getTransferPaneState(oldState.pane, {...oldState, containerContents})) ??
        {};
      return {
        ...oldState,
        containerContents,
        ...updatedPaneState,
      };
    });
  };

  renderPane(): JSX.Element | null {
    switch (this.state.pane) {
      case MODAL_PANE.CHOOSE_TRANSFER_INPUT:
        return (
          <div {...getTestProps({component: 'ChooseInputPane', element: 'root'})}>
            {this.props.inputPaneInfoBannerMessage && (
              <div className={css(styles.infoBanner)}>
                <InlineBanner
                  bannerType={InlineBannerType.INFO}
                  dataTestAttrs={{component: 'TransferModal', element: 'infoBanner'}}
                  text={this.props.inputPaneInfoBannerMessage}
                />
              </div>
            )}
            <ChooseInputPane
              paneState={TransferModalUtil.getTransferInputPaneState(this.state)}
              registryId={this.props.registryId}
              sampleStatusWarningParams={{
                ...this.baseSampleStatusTrackingParams,
                shouldShowOutputWarnings: this.props.flow === TransferFlow.TRANSFER_IN,
              }}
              onUpdateSampleStatusState={this.handleUpdateSampleStatusState}
              onUpdateModalState={this.handleUpdateState}
            />
          </div>
        );
      case MODAL_PANE.CHOOSE_TRANSFER_OUTPUT:
        return (
          <ChooseOutputPane
            paneState={TransferModalUtil.getTransferOutputPaneState(this.state)}
            registryId={this.props.registryId}
            sampleStatusWarningParams={{
              ...this.baseSampleStatusTrackingParams,
              shouldShowInputWarnings: this.props.flow === TransferFlow.TRANSFER_OUT,
            }}
            onUpdateSampleStatusState={this.handleUpdateSampleStatusState}
            onUpdateModalState={this.handleUpdateState}
          />
        );
      case MODAL_PANE.CONFIRM_TRANSFER_QUANTITIES:
        return (
          <ConfirmTransferQuantitiesPane
            paneState={TransferModalUtil.getTransferQuantitiesPaneState(this.state)}
            onUpdateModalState={this.handleUpdateState}
          />
        );
      case MODAL_PANE.CONFIRM_CONCENTRATIONS:
        return (
          <ConfirmConcentrationsPane
            paneState={TransferModalUtil.getTransferConcentrationsPaneState(this.state)}
            onChangeShouldCalculate={this.handleChangeShouldCalculateConcentrations}
            onChangeConcentration={this.handleChangeConcentration}
            allowRecalculateConcentrations={true}
            getConfirmConcentrationArgsAndCallbacks={() => ({
              ...TransferModalUtil.getConfirmConcentrationsArgs(this.state),
              onStart: this.handleCalculateConcentrationsStarted,
              onComplete: this.handleCalculateConcentrationsCompleted,
              onFailure: this.handleCalculateConcentrationsFailed,
            })}
          />
        );
      case MODAL_PANE.CONFIRM_SAMPLE_CONTROLS:
        return (
          <ConfirmSampleControlsPane
            paneState={TransferModalUtil.getTransferSampleControlsPaneState(this.state)}
            onUpdateModalState={this.handleUpdateState}
          />
        );
      default:
        assert(false, `Invalid pane: ${this.state.pane}`);
    }
  }

  override render(): JSX.Element | null {
    if (!this.state.pane) {
      return null;
    }

    return (
      <Modal
        buttons={this.getButtons()}
        closeableOnEscapeKey={false}
        onRequestClose={this.handleClose}
        onRequestSubmit={this.getSubmit()}
        title={this.props.title}
        bannerProps={this.errorBannerProps()}
      >
        <BenchlingQuery<UnitTypesQuery>
          query={UNIT_TYPES_QUERY}
          renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
        >
          {/* we query here to populate the Apollo cache so it can be read directly in nested components */}
          {() => this.renderPane()}
        </BenchlingQuery>
      </Modal>
    );
  }
}

const styles = StyleSheet.create({
  infoBanner: {
    marginBottom: 8,
  },
});

export default TransferModal;
